/**
 * State Sync Adapter
 *
 * Wrapper für bidirektionale State-Synchronisation:
 * - Local: eventBus (zwischen Komponenten im Main Window)
 * - External: postMessage (zu externem Window)
 *
 * Verwendung:
 *   const { syncState } = useStateSync('nebenkostenabrechnung');
 *   syncState('templateSourceInvoiceId', selectedId);
 */

import { onMounted, onUnmounted, toRaw } from 'vue';

import { eventBus, type StateChangePayload } from '@/configs/eventBus';

import { useExternalWindow } from './useExternalWindow';

/**
 * Initialisiert State-Sync für eine Entity
 * @param entityKey z.B. "nebenkostenabrechnung" - muss dem contentModeKey entsprechen
 * @param sender z.B. "leftPanel", "rightPanel" - zur Vermeidung von Echos
 */
export function useStateSync(entityKey: string, sender: string = 'component') {
  const { getExternalWindow } = useExternalWindow();

  /**
   * Sendet State-Change zu allen Kontexten
   * 1. Local: eventBus an andere Komponenten im Main Window
   * 2. External: postMessage zum externen Window (falls offen)
   */
  const syncState = (key: string, value: any) => {
    // 🔄 Convert Proxy to raw JavaScript object for postMessage compatibility
    const rawValue = toRaw(value);

    const payload: StateChangePayload = {
      key,
      value: rawValue,
      sender,
    };

    // Local: emit to eventBus (other components in main window)
    eventBus.emit('stateSync', payload);

    // 2️⃣ External: Send to external window via postMessage
    const externalWindow = getExternalWindow(entityKey);
    if (externalWindow && !externalWindow.closed) {
      const message = {
        type: 'STATE_SYNC',
        entityKey,
        payload,
      };

      externalWindow.postMessage(message, window.location.origin);
    }
  };

  /**
   * Listen für State-Changes vom External Window
   * Wird in onMounted registriert
   */
  const handleExternalMessage = (event: MessageEvent) => {
    // Security: Nur Messages vom gleichen Origin akzeptieren
    if (event.origin !== window.location.origin) {
      return;
    }

    // Nur STATE_SYNC Messages behandeln
    if (event.data?.type !== 'STATE_SYNC') return;

    const { entityKey: messageEntityKey, payload } = event.data;

    // Only process if it's the correct entity
    if (messageEntityKey !== entityKey) return;

    // Emit zu lokalen Listenern
    eventBus.emit('stateSync', payload);
  };

  /**
   * Registriere Listener beim Mount, cleanup beim Unmount
   */
  onMounted(() => {
    window.addEventListener('message', handleExternalMessage);
  });

  onUnmounted(() => {
    window.removeEventListener('message', handleExternalMessage);
  });

  return {
    syncState,
  };
}

/**
 * Hook zur Verwendung im externen Window
 * Lauscht auf State-Changes vom Main Window und emittet lokal
 */
export function useStateSyncExternal(entityKey: string) {
  const handleParentMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'STATE_SYNC') return;

    const { payload } = event.data;

    // Emit to local listeners in external window
    eventBus.emit('stateSync', payload);
  };

  onMounted(() => {
    window.addEventListener('message', handleParentMessage);
  });

  onUnmounted(() => {
    window.removeEventListener('message', handleParentMessage);
  });

  return {
    // External window can also write back to parent
    syncState: (key: string, value: any, sender: string = 'externalWindow') => {
      const payload: StateChangePayload = {
        key,
        value,
        sender,
      };

      // Send back to parent window with entityKey
      window.opener?.postMessage(
        {
          type: 'STATE_SYNC',
          entityKey,
          payload,
        },
        window.location.origin,
      );

      // Auch lokal emittieren
      eventBus.emit('stateSync', payload);
    },
  };
}
